// src/pages/Fotos.jsx
// Foto-Upload + Lightbox-Modal pro Prüfpunkt.
// Bild-Komprimierung clientseitig (max 1500px, JPEG q=0.8) damit Storage nicht voll wird.
// Speicherung: Storage-Bucket „fotos", DB-Tabelle „protokoll_fotos".

import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { C } from './formui';
import { t } from '../lib/sprache';
import { fotoVormerken, fotoVerwerfen, wartendeFuerPruefpunkt, fotosAbarbeiten, beiFotoAenderung, starteFotoAutomatik } from '../lib/fotoWarteschlange';
import { frage } from './Frage';

// === Helpers ===============================================================

// Bild aus File komprimieren auf max 1500px Längstkante, JPEG q=0.8
async function compressImage(file, maxSize = 1500, quality = 0.8) {
  return new Promise((resolve, reject) => {
    // Seit v57 ueber eine Objekt-Adresse statt einer Data-URL (ein 12-MP-Foto
    // waren ~7 MB Text zusaetzlich) - und die Leinwand wird danach auf 0x0
    // gesetzt. Nach vielen Fotos lehnte das iPhone sonst weitere Leinwaende ab.
    const img = new Image();
    const adresse = URL.createObjectURL(file);
    const aufraeumen = () => { try { URL.revokeObjectURL(adresse); } catch { /* egal */ } };
    img.onload = () => {
      aufraeumen();
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        if (width > height) { height = Math.round(height * maxSize / width); width = maxSize; }
        else { width = Math.round(width * maxSize / height); height = maxSize; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        canvas.width = 0;
        canvas.height = 0;
        if (!blob) reject(new Error(t('Komprimierung fehlgeschlagen')));
        else resolve(blob);
      }, 'image/jpeg', quality);
    };
    img.onerror = () => { aufraeumen(); reject(new Error(t('Bild kann nicht geladen werden'))); };
    img.src = adresse;
  });
}

// Service-Funktionen
export const fotos = {
  async listByPruefpunkt(pruefpunktId) {
    const { data, error } = await supabase
      .from('protokoll_fotos')
      .select('*')
      .eq('pruefpunkt_id', pruefpunktId)
      .order('erstellt_am', { ascending: true });
    return { data: data || [], error };
  },

  async listByStation(stationId) {
    const { data, error } = await supabase
      .from('protokoll_fotos')
      .select('*')
      .eq('station_id', stationId)
      .order('erstellt_am', { ascending: false });
    return { data: data || [], error };
  },

  // Upload: komprimiert, in Storage hoch, DB-Eintrag schreiben
  async upload(file, { stationId, protokollId, pruefpunktId, titel, userId }) {
    if (!stationId) throw new Error('stationId fehlt');
    const id = crypto.randomUUID();
    const blob = await compressImage(file, 1500, 0.8);
    const ext = 'jpg';
    const path = `stationen/${stationId}/${id}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from('fotos')
      .upload(path, blob, { upsert: false, contentType: 'image/jpeg' });
    if (upErr) throw upErr;
    const { data: urlData } = supabase.storage.from('fotos').getPublicUrl(path);
    const publicUrl = urlData.publicUrl;
    const { data, error } = await supabase
      .from('protokoll_fotos')
      .insert({
        id, station_id: stationId,
        protokoll_id: protokollId || null,
        pruefpunkt_id: pruefpunktId || null,
        titel: titel || file.name.replace(/\.[^.]+$/, ''),
        dateiname: file.name,
        storage_path: path,
        public_url: publicUrl,
        in_pdf: true,
        erstellt_von_id: userId || null,
      })
      .select()
      .single();
    if (error) {
      // Cleanup Storage falls DB-Insert scheitert
      try { await supabase.storage.from('fotos').remove([path]); } catch {}
      throw error;
    }
    return data;
  },

  async update(id, patch) {
    const { data, error } = await supabase
      .from('protokoll_fotos')
      .update(patch)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async remove(id) {
    // Storage-Path holen, DB löschen, Storage löschen
    const { data: f } = await supabase.from('protokoll_fotos').select('storage_path').eq('id', id).single();
    const { error } = await supabase.from('protokoll_fotos').delete().eq('id', id);
    if (error) throw error;
    if (f?.storage_path) {
      try { await supabase.storage.from('fotos').remove([f.storage_path]); } catch {}
    }
  },
};

// === Fotos eines Protokolls: EINE Abfrage fuer alle Pruefpunkte ============
// Vorher fragte jeder Foto-Knopf einzeln - bei jedem Kapitelwechsel 20-30
// Anfragen, die mit dem Speichern der Antworten um die Leitung konkurrierten.
// Jetzt teilen sich alle Knoepfe eines Protokolls eine Abfrage (1 Minute gueltig,
// nach Hochladen/Loeschen frisch).
const protokollFotos = new Map();   // protokollId -> { zeit, promise }
// Ein Foto aus dem Geraet ist angekommen (oder verworfen): gemerkte Liste
// verwerfen. Dieser Hoerer ist vor denen der Foto-Knoepfe angemeldet - die
// laden danach mit reload(false) und teilen sich EINE neue Abfrage.
beiFotoAenderung((d) => { if (d.protokollId) protokollFotos.delete(d.protokollId); else protokollFotos.clear(); });
function fotosDesProtokolls(protokollId, frisch = false) {
  const e = protokollFotos.get(protokollId);
  if (!frisch && e && Date.now() - e.zeit < 60000) return e.promise;
  const promise = supabase.from('protokoll_fotos').select('*')
    .eq('protokoll_id', protokollId).order('erstellt_am', { ascending: true })
    .then(({ data, error }) => { if (error) throw error; return data || []; });
  protokollFotos.set(protokollId, { zeit: Date.now(), promise });
  promise.catch(() => { if (protokollFotos.get(protokollId)?.promise === promise) protokollFotos.delete(protokollId); });
  return promise;
}

// === Hook: Fotos zu einem Prüfpunkt =======================================
export function useFotosForPruefpunkt(pruefpunktId, protokollId, kategorie) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // frisch: nach Hochladen/Aendern/Loeschen nicht den gemerkten Stand nehmen.
  const reload = async (frisch = true) => {
    if (!pruefpunktId && !kategorie) { setItems([]); setLoading(false); return; }
    setLoading(true);
    // try/finally: Egal was passiert, "laedt" hoert auf. Bei einem Fehler
    // bleiben die bisher angezeigten Fotos stehen, statt zu verschwinden.
    try {
      if (protokollId) {
        const alle = await fotosDesProtokolls(protokollId, frisch);
        setError(null);
        const eigene = alle.filter(f => (kategorie ? f.kategorie === kategorie : f.pruefpunkt_id === pruefpunktId));
        setItems(alt => (alt.length === 0 && eigene.length === 0 ? alt : eigene));
      } else {
        const { data, error } = await fotos.listByPruefpunkt(pruefpunktId);
        if (error) setError(error.message);
        else { setError(null); setItems(data); }
      }
    } catch (e) {
      setError(e.message || t('Fotos konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(false); }, [pruefpunktId, protokollId, kategorie]);

  return { items, loading, error, reload };
}

// === Lightbox-Modal: Bild groß, Titel-Edit, Mitdruck-Haken, Download, Löschen
function Lightbox({ foto, onClose, onUpdated, onRemoved }) {
  const [titel, setTitel] = useState(foto.titel || '');
  const [inPdf, setInPdf] = useState(!!foto.in_pdf);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await fotos.update(foto.id, { titel, in_pdf: inPdf });
      onUpdated(updated);
    } catch (e) {
      alert(t('Fehler beim Speichern: {fehler}', { fehler: e.message }));
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setSaving(true);
    try {
      await fotos.remove(foto.id);
      onRemoved(foto.id);
      onClose();
    } catch (e) {
      alert(t('Fehler beim Löschen: {fehler}', { fehler: e.message }));
      setSaving(false);
    }
  };

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = foto.public_url;
    a.download = foto.dateiname || (foto.titel || 'foto') + '.jpg';
    a.target = '_blank';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: C.white, borderRadius: 12, maxWidth: 720, width: '100%', maxHeight: '90vh', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 8, background: C.gray100 }}>
          <img src={foto.public_url} alt={titel} style={{ width: '100%', maxHeight: 480, objectFit: 'contain', borderRadius: 8, background: C.navy }}/>
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.gray500, textTransform: 'uppercase', marginBottom: 4 }}>{t('Titel')}</div>
          <input type="text" value={titel} onChange={e => setTitel(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 7, border: '1.5px solid ' + C.gray200, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}/>
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={inPdf} onChange={e => setInPdf(e.target.checked)} id={'in-pdf-' + foto.id}/>
            <label htmlFor={'in-pdf-' + foto.id} style={{ fontSize: 15, color: C.navy, cursor: 'pointer' }}>{t('Im PDF mitdrucken')}</label>
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <button onClick={handleDelete} disabled={saving}
              style={{ padding: '8px 14px', background: confirmDelete ? C.red : 'none', color: confirmDelete ? C.white : C.red, border: '1px solid ' + C.red, borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>
              {confirmDelete ? t('Wirklich löschen?') : '🗑️ ' + t('Löschen')}
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleDownload} style={{ padding: '8px 14px', background: C.gray100, color: C.navy, border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>
                ⬇ {t('Download')}
              </button>
              <button onClick={onClose} style={{ padding: '8px 14px', background: 'none', color: C.gray500, border: '1px solid ' + C.gray200, borderRadius: 7, cursor: 'pointer', fontSize: 15 }}>{t('Schließen')}</button>
              <button onClick={save} disabled={saving}
                style={{ padding: '8px 16px', background: C.yellow, color: C.navy, border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 800, fontSize: 15 }}>
                {saving ? t('Speichert...') : t('Speichern')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// === Hauptkomponente: Foto-Upload für einen Prüfpunkt =====================
// Zeigt einen kompakten Button + Thumbnails der vorhandenen Fotos. Bei Klick auf Thumbnail → Lightbox.
// onAnzahl (optional): meldet dem Protokoll-Editor, wie viele Fotos an diesem
// Pruefpunkt haengen. Damit kann er bei "n.i.O." erkennen, ob ueberhaupt ein
// Beleg vorliegt (Grund, Bemerkung ODER Foto).
// kategorie + titel: Fotos ohne Pruefpunkt (Typenschild, Schaltplan - Anna-Lena
// 21.09.2026). titel ist dann fest vorgegeben, sonst der Dateiname.
export function FotoUploadPruefpunkt({ pruefpunktId, stationId, protokollId, userId, onAnzahl, kategorie, titel: festerTitel }) {
  const { items, reload } = useFotosForPruefpunkt(pruefpunktId, protokollId, kategorie);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [activeFoto, setActiveFoto] = useState(null);
  const fileInputRef = useRef(null);

  // Fotos, die noch im Geraet auf Netz warten (Vorschlag 10). Angezeigt aus
  // dem Geraet selbst, mit Sanduhr - bzw. rot, wenn der Server ablehnte.
  const [wartende, setWartende] = useState([]);
  useEffect(() => {
    starteFotoAutomatik();
    let aktiv = true;
    let urls = [];
    const laden = async () => {
      let liste;
      try { liste = await wartendeFuerPruefpunkt(pruefpunktId, kategorie); } catch { return; }   // Geraet gerade nicht lesbar
      if (!aktiv) return;
      urls.forEach(u => URL.revokeObjectURL(u));
      urls = [];
      setWartende(liste.map(f => {
        const url = URL.createObjectURL(f.blob);
        urls.push(url);
        return { id: f.id, url, fehler: f.fehler, protokollId: f.protokollId };
      }));
    };
    laden();
    const abmelden = beiFotoAenderung((d) => {
      if (d.protokollId && protokollId && d.protokollId !== protokollId) return;
      laden();
      reload(false);   // ein gesendetes Foto erscheint jetzt vom Server (Liste ist verworfen)
    });
    return () => { aktiv = false; abmelden(); urls.forEach(u => URL.revokeObjectURL(u)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pruefpunktId, protokollId, kategorie]);

  const anzahl = (items || []).length + wartende.length;
  useEffect(() => { if (onAnzahl) onAnzahl(anzahl); }, [anzahl, onAnzahl]);

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true); setError(null);
    try {
      // Erst ALLE Fotos im Geraet ablegen - das geht ohne Netz. Gesendet wird
      // danach im Hintergrund; ohne Empfang geht es spaeter automatisch raus.
      for (const f of files) {
        if (!f.type.startsWith('image/')) continue;
        const blob = await compressImage(f, 1500, 0.8);
        await fotoVormerken(blob, { stationId, protokollId, pruefpunktId, kategorie, userId,
          titel: festerTitel || f.name.replace(/\.[^.]+$/, ''), dateiname: f.name });
      }
    } catch (e2) {
      setError(e2.message || t('Foto konnte nicht gespeichert werden'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
    fotosAbarbeiten().catch(() => {});
  };

  // Klar beschriftete Knoepfe statt "OK = ..., Abbrechen = ..." (Pruefung 20.09.2026).
  const wartendesAntippen = async (w) => {
    const wahl = await frage(w.fehler ? {
      titel: t('Foto vom Server abgelehnt'),
      text: w.fehler,
      knoepfe: [
        { wert: 'senden', text: t('Noch einmal senden'), art: 'haupt' },
        { wert: 'loeschen', text: t('Foto löschen'), art: 'gefahr' },
      ],
    } : {
      titel: t('Foto wartet auf Netz'),
      text: t('Dieses Foto ist noch nicht hochgeladen. Es geht automatisch raus, sobald Netz da ist.'),
      knoepfe: [{ wert: 'loeschen', text: t('Foto löschen'), art: 'gefahr' }],
      abbrechenText: t('Behalten'),
    });
    if (wahl === 'senden') fotosAbarbeiten({ erneut: true }).catch(() => {});
    if (wahl === 'loeschen') await fotoVerwerfen(w.id);
  };

  return (
    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      {/* Groesse passend zu den Antwortknoepfen - vorher war der Knopf mit
          11 px Schrift und 6 px Innenabstand deutlich zu klein dafuer. */}
      <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
        style={{ minHeight: 48, padding: '10px 16px', background: C.white, border: '2px dashed ' + C.gray300, borderRadius: 8, cursor: 'pointer', fontSize: 14, color: C.gray700, fontWeight: 700, touchAction: 'manipulation' }}>
        {/* 'Foto hinzufuegen' statt 'Foto': Mehrere Bilder gehen laengst, die
            Monteure hielten den Knopf aber fuer 'genau eines' (Anna-Lena 14.10.2026).
            In der Galerie lassen sich mehrere auf einmal markieren. */}
        {uploading ? '⏳ ' + t('speichert …')
          : anzahl > 0 ? '📷 ' + t('Weiteres Foto') + ' (' + anzahl + ')'
          : '📷 ' + t('Foto hinzufügen')}
      </button>
      {/* Bewusst OHNE capture="environment". Mit dem Attribut öffnet das Telefon
          sofort die Kamera und lässt nichts anderes zu. Wer das Bauteil vorher
          schon fotografiert hat, kam an seine Galerie nicht heran. Jetzt fragt
          das Telefon: Kamera oder vorhandenes Bild. */}
      <input ref={fileInputRef} type="file" accept="image/*" multiple
             onChange={handleFile} style={{ display: 'none' }}/>
      {items.map(f => (
        <button key={f.id} type="button" onClick={() => setActiveFoto(f)}
          title={f.titel || ''}
          style={{ padding: 0, background: 'none', border: f.in_pdf ? '2px solid ' + C.green : '2px solid ' + C.gray300, borderRadius: 6, cursor: 'pointer', overflow: 'hidden', width: 48, height: 48, flexShrink: 0 }}>
          <img src={f.public_url} alt={f.titel || ''} loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}/>
        </button>
      ))}
      {wartende.map(w => (
        <button key={w.id} type="button" onClick={() => wartendesAntippen(w)}
          title={w.fehler ? t('Vom Server abgelehnt') : t('Wartet auf Netz')}
          style={{ position: 'relative', padding: 0, background: 'none', border: '2px dashed ' + (w.fehler ? C.red : C.orange), borderRadius: 6, cursor: 'pointer', overflow: 'hidden', width: 48, height: 48, flexShrink: 0 }}>
          <img src={w.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: 0.75 }}/>
          <span style={{ position: 'absolute', right: 1, bottom: 1, fontSize: 13, lineHeight: 1, background: C.white, borderRadius: 4, padding: '1px 2px' }}>
            {w.fehler ? '❗' : '⏳'}
          </span>
        </button>
      ))}
      {wartende.length > 0 && (
        <span style={{ fontSize: 13, color: wartende.some(w => w.fehler) ? C.red : C.gray500 }}>
          {wartende.some(w => w.fehler) ? t('Foto abgelehnt – antippen') : t('wartet auf Netz')}
        </span>
      )}
      {error && <span style={{ fontSize: 13, color: C.red }}>! {error}</span>}
      {activeFoto && (
        <Lightbox
          foto={activeFoto}
          onClose={() => setActiveFoto(null)}
          onUpdated={(updated) => { setActiveFoto(updated); reload(); }}
          onRemoved={() => { setActiveFoto(null); reload(); }}
        />
      )}
    </div>
  );
}

// === Foto-Feld ohne Pruefpunkt (Typenschild, Schaltplan) =====================
// Anna-Lena 21.09.2026: "beim Ausfuellen bei MS und Trafo den Upload Typenschild,
// ebenso beim MS-Uebersichtsschaltplan". Gleicher Weg wie die Pruefpunkt-Fotos
// (auch ohne Netz), unterschieden ueber protokoll_fotos.kategorie (Migration 63).
export function FotoFeld({ label, kategorie, titel, stationId, protokollId, userId }) {
  if (!protokollId || !stationId) return null;
  return (
    <div style={{ marginTop: 10, padding: '8px 12px', background: C.gray100, borderRadius: 8 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{label}</div>
      <FotoUploadPruefpunkt kategorie={kategorie} titel={titel} stationId={stationId}
        protokollId={protokollId} userId={userId}/>
    </div>
  );
}

// === Hook: Fotos zu einer Station ==========================================
export function useFotosForStation(stationId) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = async () => {
    if (!stationId) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error } = await fotos.listByStation(stationId);
      if (error) setError(error.message);
      else { setError(null); setItems(data); }
    } catch (e) {
      setError(e.message || t('Fotos konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [stationId]);

  return { items, loading, error, reload };
}

// === Galerie auf Station-Detail-Seite mit ZIP-Download =====================
// Zeigt alle Fotos einer Station als Thumbnails-Grid. Pro Foto: Titel + PDF-Haken-Anzeige.
// Klick aufs Thumbnail → Lightbox-Modal (gleich wie am Prüfpunkt).
// Oben: „Alle als ZIP“ — lädt alle Fotos der Station in einem ZIP-Archiv.
export function FotosGalerieStation({ stationId, stationsBezeichnung }) {
  const { items, loading, reload } = useFotosForStation(stationId);
  const [activeFoto, setActiveFoto] = useState(null);
  const [zipping, setZipping] = useState(false);
  const [zipError, setZipError] = useState(null);
  // Bilder auch OHNE laufendes Protokoll: das Buero kann Fotos zur Station
  // vorab ablegen, damit die Monteure vorbereitet hinfahren.
  const [uploading, setUploading] = useState(false);
  const [uploadFehler, setUploadFehler] = useState(null);
  const dateiFeld = useRef(null);

  const dateienHochladen = async (e) => {
    const dateien = Array.from(e.target.files || []);
    if (dateien.length === 0) return;
    setUploading(true); setUploadFehler(null);
    try {
      for (const d of dateien) {
        if (!d.type.startsWith('image/')) continue;
        await fotos.upload(d, { stationId });
      }
      await reload();
    } catch (e2) {
      setUploadFehler(e2.message || t('Hochladen fehlgeschlagen'));
    } finally {
      setUploading(false);
      if (dateiFeld.current) dateiFeld.current.value = '';
    }
  };

  const handleZipDownload = async () => {
    setZipping(true); setZipError(null);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      // Foto-URLs nacheinander fetchen und ins ZIP packen
      for (let i = 0; i < items.length; i++) {
        const f = items[i];
        try {
          const res = await fetch(f.public_url);
          if (!res.ok) continue;
          const blob = await res.blob();
          // Dateiname: 001_titel.jpg (mit Lauf-Nummer für sortierbare Reihenfolge)
          const num = String(i + 1).padStart(3, '0');
          const safeTitel = (f.titel || 'foto').replace(/[^a-zA-Z0-9_äöüÄÖÜß-]/g, '_').slice(0, 50);
          zip.file(`${num}_${safeTitel}.jpg`, blob);
        } catch { /* skip on error */ }
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (stationsBezeichnung || 'Station').replace(/[^a-zA-Z0-9_äöüÄÖÜß-]/g, '_');
      a.download = `Fotos_${safeName}_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setZipError(e.message || t('ZIP-Erstellung fehlgeschlagen'));
    } finally { setZipping(false); }
  };

  if (loading) {
    return (
      <div style={{ background: C.white, borderRadius: 10, boxShadow: '0 1px 6px rgba(10,22,40,0.06)', padding: 18, marginBottom: 16, color: C.gray500, fontSize: 15 }}>
        {t('Lade Bilder...')}
      </div>
    );
  }

  return (
    <div style={{ background: C.white, borderRadius: 10, boxShadow: '0 1px 6px rgba(10,22,40,0.06)', padding: 18, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 800, color: C.navy, fontSize: 15 }}>
          {t('Bilder')} <span style={{ color: C.gray500, fontWeight: 600 }}>({items.length})</span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => dateiFeld.current?.click()} disabled={uploading}
          style={{ minHeight: 40, padding: '7px 14px', background: C.white, border: '2px dashed ' + C.gray300, borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: 14, color: C.gray700 }}>
          {uploading ? '⬆ ' + t('lädt hoch …') : '📷 ' + t('Bild hinzufügen')}
        </button>
        <input ref={dateiFeld} type='file' accept='image/*' multiple onChange={dateienHochladen} style={{ display: 'none' }}/>
        {items.length > 0 && (
          <button onClick={handleZipDownload} disabled={zipping}
            style={{ padding: '7px 14px', background: zipping ? C.gray300 : C.navyMid, color: C.yellow, border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
            {zipping ? t('Erstelle ZIP...') : '⬇ ' + t('Alle als ZIP')}
          </button>
        )}
        </div>
      </div>
      {zipError && <div style={{ padding: 8, background: C.redBg, color: C.red, borderRadius: 6, marginBottom: 8, fontSize: 14 }}>! {zipError}</div>}
      {uploadFehler && <div style={{ padding: 8, background: C.redBg, color: C.red, borderRadius: 6, marginBottom: 8, fontSize: 14 }}>! {uploadFehler}</div>}
      {items.length === 0 ? (
        <div style={{ color: C.gray500, fontSize: 15, fontStyle: 'italic' }}>
          {t('Noch keine Bilder zu dieser Station. Du kannst hier direkt welche hinzufügen — oder die Monteure laden sie im Protokoll je Prüfpunkt hoch.')}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10 }}>
          {items.map(f => (
            <button key={f.id} type="button" onClick={() => setActiveFoto(f)}
              style={{ padding: 0, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 8 }}>
              <div style={{ width: '100%', aspectRatio: '1 / 1', background: C.gray100, borderRadius: 8, overflow: 'hidden', position: 'relative', border: f.in_pdf ? '2px solid ' + C.green : '2px solid ' + C.gray200 }}>
                <img src={f.public_url} alt={f.titel || ''} loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}/>
                {f.in_pdf && <span style={{ position: 'absolute', top: 4, right: 4, background: C.green, color: C.white, fontSize: 12, fontWeight: 800, padding: '2px 6px', borderRadius: 4 }}>PDF</span>}
              </div>
              <div style={{ padding: '6px 4px 0', fontSize: 13, color: C.navy, fontWeight: 600, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                {f.titel || t('(ohne Titel)')}
              </div>
              <div style={{ padding: '0 4px', fontSize: 12, color: C.gray500, textAlign: 'left', width: '100%' }}>
                {f.erstellt_am ? new Date(f.erstellt_am).toLocaleDateString('de-DE') : ''}
              </div>
            </button>
          ))}
        </div>
      )}
      {activeFoto && (
        <Lightbox
          foto={activeFoto}
          onClose={() => setActiveFoto(null)}
          onUpdated={(updated) => { setActiveFoto(updated); reload(); }}
          onRemoved={() => { setActiveFoto(null); reload(); }}
        />
      )}
    </div>
  );
}

// === Foto-Vorschau vor dem Drucken =========================================
// Anna-Lena: "Die Monteure machen zu jedem Punkt Bilder. Bevor ich das
// Protokoll drucke, moechte ich entscheiden, welches mitgedruckt wird."
// Der Haken schreibt in_pdf an dem Foto; der PDF-Export nimmt nur, was
// angehakt ist.
export function FotoVorschauFuerPdf({ protokollId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState(null);

  const laden = useCallback(async () => {
    if (!protokollId) { setLoading(false); return; }
    const { data, error } = await supabase
      .from('protokoll_fotos')
      .select('*')
      .eq('protokoll_id', protokollId)
      .order('erstellt_am');
    if (error) setFehler(error.message);
    setItems(data || []);
    setLoading(false);
  }, [protokollId]);

  useEffect(() => { laden(); }, [laden]);

  const umschalten = async (foto) => {
    const neu = !foto.in_pdf;
    // Erst im Bildschirm umschalten, damit es sich schnell anfuehlt.
    setItems(list => list.map(f => (f.id === foto.id ? { ...f, in_pdf: neu } : f)));
    try {
      await fotos.update(foto.id, { in_pdf: neu });
      setFehler(null);
    } catch (e) {
      setItems(list => list.map(f => (f.id === foto.id ? { ...f, in_pdf: !neu } : f)));
      setFehler(t('Konnte nicht gespeichert werden: {fehler}', { fehler: e.message || t('unbekannter Fehler') }));
    }
  };

  if (loading) return null;
  if (items.length === 0) return null;

  const imPdf = items.filter(f => f.in_pdf).length;

  return (
    <div style={{ marginBottom: 18, padding: 14, background: C.gray100, borderRadius: 8 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: C.navy, marginBottom: 4 }}>
        {t('Fotos im Protokoll')}
      </div>
      <div style={{ fontSize: 13, color: C.gray500, marginBottom: 10 }}>
        {items.length === 1
          ? t('{imPdf} von {n} Foto wird mitgedruckt.', { imPdf, n: items.length })
          : t('{imPdf} von {n} Fotos werden mitgedruckt.', { imPdf, n: items.length })}
        {' '}{t('Tippe auf ein Bild, um es aus- oder abzuwählen.')}
      </div>
      {fehler && (
        <div style={{ padding: '8px 10px', background: C.redBg, color: C.red, borderRadius: 6,
                      fontSize: 14, fontWeight: 700, marginBottom: 8 }}>{fehler}</div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10 }}>
        {items.map(f => (
          <button key={f.id} type="button" onClick={() => umschalten(f)}
            title={f.titel || f.dateiname}
            style={{ padding: 0, border: '3px solid ' + (f.in_pdf ? C.green : C.gray200),
                     borderRadius: 9, overflow: 'hidden', background: C.white,
                     cursor: 'pointer', position: 'relative', fontFamily: 'inherit',
                     opacity: f.in_pdf ? 1 : 0.55 }}>
            <img src={f.public_url} alt={f.titel || ''} loading="lazy" decoding="async"
                 style={{ display: 'block', width: '100%', height: 100, objectFit: 'cover' }}/>
            <div style={{ padding: '5px 6px', fontSize: 12, color: C.navy, textAlign: 'left',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {f.titel || f.dateiname || t('Foto')}
            </div>
            <div style={{ position: 'absolute', top: 5, right: 5, width: 24, height: 24,
                          borderRadius: '50%', background: f.in_pdf ? C.green : 'rgba(255,255,255,0.9)',
                          color: f.in_pdf ? C.white : C.gray500,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 14, fontWeight: 800,
                          border: '1.5px solid ' + (f.in_pdf ? C.green : C.gray300) }}>
              {f.in_pdf ? '✓' : ''}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
